import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type TASApplicationModel from './tas-application.ts';
import type AttachmentModel from './attachment.ts';
import type TasAssetModel from './tas-asset.ts';
import type { TransitionLogSignature } from '../types/tuition-assistance.ts';
import { Type } from '@warp-drive/core-types/symbols';

export default class TasAssistanceModel extends Model {
  @attr declare state: string;
  @attr declare amount: number;
  @attr declare manner: string;
  @attr declare fields: Record<string, unknown>;
  @attr declare transitionLog: TransitionLogSignature[];

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('tas-application', { async: false, inverse: 'tasAssistances' })
  declare tasApplication: TASApplicationModel;

  @hasMany('attachment', { async: false, inverse: null })
  declare attachments: AttachmentModel[];

  @hasMany('tas-asset', { async: false, inverse: 'tasAssistances' })
  declare tasAssets: TasAssetModel[];

  /*************************
   **  Computed Properties **
   *************************/

  get isSettled() {
    return this.state === 'TAS.AssistanceState.SETTLED';
  }

  get isCaptured() {
    return this.state === 'TAS.AssistanceState.CAPTURED';
  }

  get isCourseCash() {
    return this.manner === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get hasCardUsageProof(): boolean {
    // Check displayClassification which returns classification || assetClassification
    // The asset-classification comes from the TasAsset, not the Attachment join record
    return (
      this.attachments?.some(
        (attachment) => attachment.displayClassification === 'CARD_USAGE_PROOF'
      ) ?? false
    );
  }

  get needsReceipt() {
    return this.isCourseCash && this.isCaptured && !this.hasCardUsageProof;
  }

  get hasReceiptUploaded() {
    return this.hasCardUsageProof;
  }

  get capturedDate(): Date | null {
    const capturedTransition = this.transitionLog?.find(
      (log: TransitionLogSignature) => log.to === 'TAS.AssistanceState.CAPTURED'
    );
    return capturedTransition ? new Date(capturedTransition.timestamp) : null;
  }

  get receiptDueDate(): Date | null {
    const captured = this.capturedDate;
    if (!captured) return null;

    const dueDate = new Date(captured);
    dueDate.setDate(dueDate.getDate() + 14); // 14 days after capture
    return dueDate;
  }

  get daysUntilReceiptDue(): number | null {
    const due = this.receiptDueDate;
    if (!due) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);

    const diffTime = dueDay.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  get settledDate(): string | null {
    const settledTransition = this.transitionLog?.find(
      (log: TransitionLogSignature) => log.to === 'TAS.AssistanceState.SETTLED'
    );

    return settledTransition
      ? ((settledTransition.reason?.payment?.date || settledTransition.timestamp) as string)
      : null;
  }

  [Type] = 'tas-assistance' as const;
}
